import { useCallback, useState } from "react"
import Modal from "react-bootstrap/Modal"
import Cropper, { type Area } from "react-easy-crop"
import { useTranslation } from "react-i18next"
import { FaCamera } from "react-icons/fa"

import Button from "@/components/Button/Button"
import Error from "@/components/Error/Error"
import type { StudentProfile } from "@/types/profileTypes"
import { getCroppedImg } from "@/utils/helpers"
import studentImg from "/assets/images/studentImg.png"

import styles from "./PicInput.module.css"

interface PicImageProps {
  student?: boolean
  values: StudentProfile
  name: keyof StudentProfile
  setFieldValue: (field: string, value: unknown) => void
  oldFile: string
  backError?: string
  error?: React.ReactNode
}

export default function PicImg({
  student,
  values,
  name,
  setFieldValue,
  oldFile,
  backError,
  error,
}: PicImageProps) {
  const { t } = useTranslation()

  const [imageSrc, setImageSrc] = useState("")
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
  const [showCropper, setShowCropper] = useState(false)

  const onCropComplete = useCallback((_: unknown, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleCropDone = async () => {
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels!)
    setFieldValue(name, croppedImage)
    setShowCropper(false)
  }

  return (
    <div className="text-center d-flex flex-column gap-2">
      <label
        htmlFor={`edit-profile-img-inp-${name}`}
        className={`${styles["profile__pic-cont"]} ${student && styles["student"]} d-flex align-items-center justify-content-center `}
      >
        <div className={styles["pic__icon"]}>
          <FaCamera size={16} color="white" />
        </div>

        {values[name] || oldFile ? (
          <div className={styles.profile__pic}>
            <img
              className={"w-100 h-100 rounded-circle object-fit-cover"}
              src={
                typeof values[name] === "object"
                  ? URL.createObjectURL(values[name])
                  : String(values[name])
              }
              alt=""
            />
          </div>
        ) : (
          <div className="d-flex flex-column gap-2 align-items-center">
            <img src={studentImg} alt="" />
          </div>
        )}
      </label>
      <input
        id={`edit-profile-img-inp-${name}`}
        type="file"
        accept="image/*"
        className={styles["edit-img__inp"]}
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (file) {
            const imageDataUrl = (await readFile(file)) as string
            setImageSrc(imageDataUrl)
            setShowCropper(true)
          }
        }}
      />

      {backError || error ? <Error>{backError || error}</Error> : null}

      {/* Crop Modal */}
      <Modal
        show={showCropper}
        onHide={() => setShowCropper(false)}
        size="lg"
        centered
      >
        <Modal.Body style={{ position: "relative", height: "400px" }}>
          <div className="cropper-container">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="cropper-overlay" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCropDone}>
            {t("buttons.save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener("load", () => resolve(reader.result))
    reader.readAsDataURL(file)
  })
}
